import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import {
    CAMERA_LOOKAT_Y,
    CAMERA_RADIUS
} from '../variable/constant';
import {
    CommandManager,
    AddItemCommand,
    SetBindingTypeCommand,
    SetTouchHelperCommand,
    SetBindingYCommand,
    setAllBindingYCommand,
    SetSizeCommand,
    ReomveCommand,
    CopyCommand,
    ReplaceCommand,
    RotateCommand,
    SetZoomCommand,
    SetAngleCommand,
    MoveCommand,
    ClearCommand,
    CreateImageCommand,
} from '../class/index.js'
import { TransformControls as pcObjectControl } from '../three/pcObjectControl';
import { TransformControls as mobileObjectControl } from '../three/mobileObjectControl';
import renderReal from '../render/renderReal';
import renderModel from '../render/renderModel';
import { pickContainerObject, pickFlowerObject, selectFlower } from '../utils/index'
const { getContainerSaveData } = require('../utils/artworkData');
const { CONTAINER_LIGHT_LAYER, CONTAINER_LIGHT_OVERLAY_PRESET } = require('../utils/containerLight');
//将three设置到window中方便使用
window.THREE = THREE;

function clampNumber(value, min, max, fallback) {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) {
        return fallback;
    }
    return Math.min(max, Math.max(min, numberValue));
}

function createStudioBackgroundAdjust(config = {}) {
    return {
        x: clampNumber(config.x, -300, 300, 0),
        y: clampNumber(config.y, -300, 300, 0),
        scale: clampNumber(config.scale, 0.8, 2, 1),
        brightness: clampNumber(config.brightness, 45, 160, 100),
        contrast: clampNumber(config.contrast, 50, 150, 100)
    };
}

class Editor {
    constructor(config) {
        let { dom, plat, readOnly, editorType } = config;
        /* 初始化内置字段 */

        //初始化设置
        this.params = {
            //dom
            dom: typeof dom === 'string' ? document.getElementById(dom) : dom,
            //平台 pc 电脑鼠标 mobile 移动触摸
            plat: plat || 'pc',
            //是否只读
            readOnly: readOnly || false,
            //编辑器类型 free3d 自由编辑器 / studio 棚景编辑器
            editorType: editorType === 'studio' ? 'studio' : 'free3d',
            //渲染相关
            render: {
                //放大倍数
                zoom: 1,
                //困扎方式   chineseStyle 中式 westStyle西式
                bindingType: 'chineseStyle',
                //显示焦点辅助点
                touchHelper: true,
                //器皿光感增强，默认关闭，试验开关手动开启
                containerLight: false
            },
            moving: false,
            draggingFlowerId: '',
            canvasLock: false,
            showId: '',
            background: '',
            studio: {
                backgroundType: 'warm',
                backgroundImage: '',
                backgroundAdjust: {
                    x: 0,
                    y: 0,
                    scale: 1,
                    brightness: 100,
                    contrast: 100
                }
            }
        };

        //three相关对象
        this.three = {
            //摄像机
            camera: null,
            //视角控制器
            viewController: null,
            //场景
            scene: null,
            //渲染器 
            renderer: null,
            //物体控制器
            objController: null,
            //状态  model 模型 real 真实
            status: ''
        };

        //物体相关对象
        this.objects = {
            //花朵（主要实例）
            flowers: [],
            //普通容器
            container: null,
            //棚景多容器
            containers: [],
            //剑山容器
            jsContainer: null,
            //光线（用于各类容器）
            lights: null,
            //器皿专用补光
            containerLightGroup: null,
            //辅助线
            supportLine: null,
            //选中素材提示
            selectionHelper: null,
            //棚景背景层
            studioBackgroundLayer: null
        }

        //用于储存移动位置
        this.tempPositions = [];
        this.selectedFlowerId = '';
        this.selectedContainerId = '';
        this.selectionHelperSuspended = false;
        this.studioGroupDrag = {
            active: false,
            pointerId: null,
            containerId: '',
            startX: 0,
            startRotationY: 0
        };

        //监听器对象
        this.listener = {};

        //资源栈
        this.resource = {};

        //渲染
        this.render = () => {
            this.three.renderer.render(this.three.scene, this.three.camera);
            this.renderContainerLightPass();
        };

        //命令管理器
        this.commandManager = new CommandManager(this);

        //帧渲染
        this.frameRender = () => {
            this.three.viewController.update();
            this.updateSelectionHelper();
            this.render();
            requestAnimationFrame(this.frameRender);
        };

        //初始化场景
        this.initScene();

        //初始化物体
        this.initObject();
        this.applyStudioMode();

        //初始化监听器
        this.initListener();
    }

    //初始化场景
    initScene () {
        //创建场景
        this.three.scene = new THREE.Scene();
        //创建渲染器
        this.three.renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
        });
        //设置渲染像素
        this.three.renderer.setPixelRatio(window.devicePixelRatio);
        //设置渲染高宽
        this.three.renderer.setSize(
            this.params.dom.clientWidth,
            this.params.dom.clientHeight
        );
        //编辑器背景透明
        this.three.renderer.setClearColor(0xffffff, 0);


        //创建摄像机
        this.three.camera = new THREE.PerspectiveCamera(
            55,
            this.params.dom.clientWidth / this.params.dom.clientHeight,
            1,
            4400
        );
        this.three.camera.position.set(0, CAMERA_LOOKAT_Y, -CAMERA_RADIUS);

        //创建视角控制器
        this.three.viewController = new OrbitControls(
            this.three.camera,
            this.three.renderer.domElement
        );
        //设置视觉中心
        this.three.viewController.target = new THREE.Vector3(
            0,
            CAMERA_LOOKAT_Y,
            0
        );
        //限制最小距离
        this.three.viewController.minDistance = 100;
        //限制最大距离
        this.three.viewController.maxDistance = CAMERA_RADIUS;
        //限制最低视角高度
        this.three.viewController.maxPolarAngle = Math.PI / 1.75;
        //禁止平移缩放
        this.three.viewController.enablePan = false;
        this.three.viewController.enableZoom = false;




        //创建物体控制器
        var ObjectControl = this.params.plat === 'pc'
            ? pcObjectControl
            : mobileObjectControl;
        this.three.objController = new ObjectControl(
            this.three.camera,
            this.three.renderer.domElement
        );
        this.three.objController.transparent = 1;
        this.three.objController.addEventListener('change', this.render);
        this.three.objController.enabled = !this.params.readOnly;
        this.three.scene.add(this.three.objController);

        //插入dom
        this.params.dom.appendChild(this.three.renderer.domElement);
        this.applyStudioMode();
        //帧渲染
        this.frameRender();

    }

    //初始化监听器
    initListener () {
        //监听尺寸改变时进行相对应的调整
        window.addEventListener('resize', () => {
            this.three.camera.aspect =
                this.params.dom.clientWidth / this.params.dom.clientHeight;
            this.three.camera.updateProjectionMatrix();
            this.three.renderer.setSize(
                this.params.dom.clientWidth,
                this.params.dom.clientHeight
            );
            this.three.renderer.setPixelRatio(window.devicePixelRatio);
        });

        //若为pc模式
        if (this.params.plat === 'pc') {
            //监听鼠标移动以选中花
            this.params.dom.addEventListener('pointermove', (event) => {
                if (this.params.moving) {
                    return;
                }
                selectFlower.call(this, event);
            });
        } else {
            //监听点击以选中花
            this.params.dom.addEventListener('pointerdown', (event) => {
                selectFlower.call(this, event);
                if (!this.selectedFlowerId) {
                    const containerObject = pickContainerObject.call(this, event);
                    if (containerObject) {
                        this.selectContainerById(containerObject.id);
                    }
                }
            });
        }
        this.three.renderer.domElement.addEventListener('pointerdown', (event) => {
            this.startStudioGroupDrag(event);
        });
        this.three.renderer.domElement.addEventListener('pointermove', (event) => {
            this.updateStudioGroupDrag(event);
        });
        this.three.renderer.domElement.addEventListener('pointerup', (event) => {
            this.endStudioGroupDrag(event);
        });
        this.three.renderer.domElement.addEventListener('pointercancel', (event) => {
            this.endStudioGroupDrag(event);
        });
        this.params.dom.addEventListener('pointerup', () => {
            this.params.moving = false;
            this.syncViewControllerMode();
            if (this.params.plat === 'mobile') {
                this.three.objController.detach()
            }
            renderReal(this);
        })

        this.three.objController.addEventListener(
            'dragging-changed',
            (event) => {
                const parentObject = event.target.object.parentObject;
                const objectPosition = parentObject.getMovePosition
                    ? parentObject.getMovePosition()
                    : event.target.object.position;
                this.tempPositions.push({ x: objectPosition.x, y: objectPosition.y, z: objectPosition.z });
                this.three.viewController.enabled = !event.value && !this.isStudioMode() && !this.params.canvasLock;
                if (event.value) {
                    this.params.draggingFlowerId = parentObject.id;
                    parentObject.clearReal();
                    this.three.status = 'model';
                } else {
                    this.params.draggingFlowerId = '';
                    this.commandManager.execute(new MoveCommand({
                        id: parentObject.id,
                        start: this.tempPositions[0],
                        end: this.tempPositions[1]
                    }, this));
                    this.tempPositions = [];
                    this.emitFlowerChoose(parentObject);
                    if (this.listener['flower-move']) {
                        this.listener['flower-move']({
                            id: parentObject.id
                        });
                    }
                    this.syncLayerState();
                }
                this.updateSelectionHelper();
            }

        );

        //通过控制器拖动模拟花头时更新根部曲线
        this.three.objController.addEventListener('objectChange', (event) => {
            this.params.moving = true;
            let targetObject = event.target.object;
            this.selectedFlowerId = targetObject.parentObject.id;
            if (targetObject.parentObject.syncControlObjectPosition) {
                targetObject.parentObject.syncControlObjectPosition(targetObject);
            }
            targetObject.parentObject.updateModel();

        })

        //视角转动时触发
        this.three.viewController.addEventListener('change', (e) => {
            if (this.isStudioMode()) {
                return;
            }
            renderModel(this);
            if (this.listener['camera-change']) {
                let rotateY = THREE.MathUtils.radToDeg(e.target.getAzimuthalAngle()),
                    rotateX = THREE.MathUtils.radToDeg(e.target.getPolarAngle());
                if (rotateY < 0) {
                    rotateY = 180 + (180 + rotateY);
                }
                this.listener['camera-change']({ rotateX, rotateY });
            }
        });

    }

    //初始化物体
    initObject () {

        //初始化剑山
        var shanMesh = new THREE.Mesh(new THREE.CylinderGeometry(40, 42, 5, 60), new THREE.MeshPhysicalMaterial({ color: 0x999999 }));
        shanMesh.position.y = 0;
        var jianMesh = new THREE.Mesh(new THREE.CylinderGeometry(35, 35, 5, 60), new THREE.MeshPhysicalMaterial({ color: 0xD7B763 }));
        jianMesh.position.y = 5;
        var jsContainer = new THREE.Group();
        jsContainer.add(shanMesh);
        jsContainer.add(jianMesh);
        this.three.scene.add(jsContainer);
        jsContainer.visible = false;
        this.objects.jsContainer = jsContainer;

        //初始化辅助线
        let supportLine = new THREE.GridHelper(30000, 1000, 0xcecece, 0xd5d5d5);
        supportLine.position.y = -5;
        this.three.scene.add(supportLine);
        this.objects.supportLine = supportLine;

        //初始化光线
        //环境光
        const envLight = new THREE.AmbientLight(0xefefef, 0.7);
        //太阳光
        const sunLight = new THREE.DirectionalLight(0xffffff, 0.4);
        sunLight.position.set(0, 1, 0);
        this.three.scene.add(sunLight);
        var lights = new THREE.Group();
        lights.add(envLight);
        lights.add(sunLight);
        this.three.scene.add(lights);
        this.objects.lights = lights;
        this.createSelectionHelper();
    }

    createSelectionHelper () {
        const canvas = document.createElement('canvas');
        canvas.width = 128;
        canvas.height = 128;
        const ctx = canvas.getContext('2d');
        const center = canvas.width / 2;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.beginPath();
        ctx.arc(center, center, 43, 0, Math.PI * 2);
        ctx.lineWidth = 14;
        ctx.strokeStyle = 'rgba(110, 159, 33, 0.18)';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(center, center, 43, 0, Math.PI * 2);
        ctx.lineWidth = 5;
        ctx.strokeStyle = 'rgba(110, 159, 33, 0.9)';
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(center, center, 55, 0, Math.PI * 2);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.stroke();

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 0.96,
            depthTest: false,
            depthWrite: false,
        });
        const helper = new THREE.Sprite(material);
        helper.name = 'selection-helper';
        helper.renderOrder = 9999;
        helper.visible = false;
        this.three.scene.add(helper);
        this.objects.selectionHelper = helper;
    }

    getSelectionHelperPosition (flowerObject) {
        if (!flowerObject || !flowerObject.modelMesh) {
            return null;
        }
        if (flowerObject.getHeadCenterPosition) {
            return flowerObject.getHeadCenterPosition();
        }
        if (flowerObject.modelMesh.touchHelperMesh) {
            return flowerObject.modelMesh.touchHelperMesh.getWorldPosition(new THREE.Vector3());
        }
        if (flowerObject.modelMesh.flowerMesh) {
            return flowerObject.modelMesh.flowerMesh.getWorldPosition(new THREE.Vector3());
        }
        return null;
    }

    getSelectionHelperScreenRect (flowerObject) {
        const position = this.getSelectionHelperPosition(flowerObject);
        if (!position || !this.params.dom || !this.three.camera) {
            return null;
        }
        const rect = this.params.dom.getBoundingClientRect();
        const screenPosition = position.clone().project(this.three.camera);
        if (screenPosition.z < -1 || screenPosition.z > 1) {
            return null;
        }
        const centerX = rect.left + ((screenPosition.x + 1) / 2) * rect.width;
        const centerY = rect.top + ((1 - screenPosition.y) / 2) * rect.height;
        const size = 48;
        return {
            id: flowerObject.id,
            left: centerX - size / 2,
            top: centerY - size / 2,
            right: centerX + size / 2,
            bottom: centerY + size / 2,
            width: size,
            height: size,
        };
    }

    emitSelectionHelperPosition (flowerObject = null) {
        if (!this.listener['selection-helper-position']) {
            return;
        }
        this.listener['selection-helper-position'](
            flowerObject ? this.getSelectionHelperScreenRect(flowerObject) : null
        );
    }

    updateSelectionHelper () {
        const helper = this.objects.selectionHelper;
        if (!helper) {
            return;
        }
        const flowerObject = this.getFlowerById(this.selectedFlowerId);
        if (
            this.selectionHelperSuspended ||
            this.params.draggingFlowerId ||
            !flowerObject ||
            !flowerObject.params.visible ||
            (this.params.showId && this.params.showId !== flowerObject.id)
        ) {
            helper.visible = false;
            this.emitSelectionHelperPosition(null);
            return;
        }
        const position = this.getSelectionHelperPosition(flowerObject);
        if (!position) {
            helper.visible = false;
            this.emitSelectionHelperPosition(null);
            return;
        }
        const distance = position.distanceTo(this.three.camera.position);
        const scale = THREE.MathUtils.clamp(distance * 0.06, 36, 92);
        helper.position.copy(position);
        helper.scale.set(scale, scale, 1);
        helper.visible = true;
        this.emitSelectionHelperPosition(flowerObject);
    }

    //添加监听器
    addEventListener (method, callback) {
        this.listener[method] = callback;
    }

    isStudioMode () {
        return this.params.editorType === 'studio';
    }

    setEditorType (type = 'free3d') {
        this.params.editorType = type === 'studio' ? 'studio' : 'free3d';
        if (!this.isStudioMode() && this.objects.containers.length > 1) {
            const activeContainer = this.getActiveContainer() || this.objects.containers[0];
            this.objects.containers
                .filter(container => container !== activeContainer)
                .forEach(container => container.remove());
            this.objects.container = activeContainer || null;
            this.objects.containers = activeContainer ? [activeContainer] : [];
            this.selectedContainerId = activeContainer ? activeContainer.id : '';
        }
        this.applyStudioMode();
    }

    syncViewControllerMode () {
        if (!this.three.viewController) {
            return;
        }
        const viewEnabled = !this.isStudioMode() && !this.params.canvasLock;
        this.three.viewController.enabled = viewEnabled;
        this.three.viewController.enableRotate = viewEnabled;
    }

    forceStudioCamera () {
        if (!this.three.viewController) {
            return;
        }
        const cameraPosition = new THREE.Vector3(0, 0, CAMERA_RADIUS);
        this.three.viewController.object.position.set(
            cameraPosition.x,
            cameraPosition.y + CAMERA_LOOKAT_Y,
            cameraPosition.z
        );
        this.three.viewController.target.set(0, CAMERA_LOOKAT_Y, 0);
        this.three.viewController.update();
    }

    normalizeStudioPreset (preset) {
        return ['white', 'warm', 'gray', 'dark'].includes(preset) ? preset : 'warm';
    }

    setStudioPreset (preset = 'warm') {
        this.params.studio.backgroundType = this.normalizeStudioPreset(preset);
        this.params.studio.backgroundImage = '';
        this.applyStudioMode();
    }

    setStudioBackground (url = '') {
        this.params.studio.backgroundImage = url || '';
        this.applyStudioMode();
    }

    setStudioBackgroundAdjust (config = {}) {
        this.params.studio.backgroundAdjust = createStudioBackgroundAdjust({
            ...this.params.studio.backgroundAdjust,
            ...config
        });
        this.applyStudioMode();
    }

    ensureStudioBackgroundLayer () {
        if (this.objects.studioBackgroundLayer && this.objects.studioBackgroundLayer.parentNode === this.params.dom) {
            return this.objects.studioBackgroundLayer;
        }
        const layer = document.createElement('div');
        layer.className = 'studio_background_layer';
        Array.from(this.params.dom.attributes || []).forEach((attr) => {
            if (/^data-v-/.test(attr.name)) {
                layer.setAttribute(attr.name, attr.value);
            }
        });
        if (this.params.dom.firstChild) {
            this.params.dom.insertBefore(layer, this.params.dom.firstChild);
        } else {
            this.params.dom.appendChild(layer);
        }
        this.objects.studioBackgroundLayer = layer;
        return layer;
    }

    applyStudioMode () {
        if (!this.params.dom) {
            return;
        }
        const layer = this.ensureStudioBackgroundLayer();
        const adjust = createStudioBackgroundAdjust(this.params.studio.backgroundAdjust);
        this.params.studio.backgroundAdjust = adjust;
        this.params.dom.style.setProperty('--studio-bg-x', `${adjust.x}px`);
        this.params.dom.style.setProperty('--studio-bg-y', `${adjust.y}px`);
        this.params.dom.style.setProperty('--studio-bg-scale', adjust.scale);
        this.params.dom.style.setProperty('--studio-bg-brightness', adjust.brightness / 100);
        this.params.dom.style.setProperty('--studio-bg-contrast', adjust.contrast / 100);

        if (this.isStudioMode()) {
            this.three.scene.background = null;
            if (this.objects.supportLine) {
                this.objects.supportLine.visible = false;
            }
            this.forceStudioCamera();
            layer.style.display = '';
            this.params.dom.classList.add('studio-background');
            ['white', 'warm', 'gray', 'dark'].forEach(preset => {
                this.params.dom.classList.remove(`studio-preset-${preset}`);
            });
            this.params.dom.classList.add(`studio-preset-${this.normalizeStudioPreset(this.params.studio.backgroundType)}`);
            if (this.params.studio.backgroundImage) {
                this.params.dom.classList.add('studio-custom-background');
                layer.style.backgroundImage = `url("${this.params.studio.backgroundImage}")`;
                layer.style.backgroundSize = 'cover';
                layer.style.backgroundPosition = 'center center';
                layer.style.backgroundRepeat = 'no-repeat';
            } else {
                this.params.dom.classList.remove('studio-custom-background');
                layer.style.backgroundImage = '';
                layer.style.backgroundSize = '';
                layer.style.backgroundPosition = '';
                layer.style.backgroundRepeat = '';
            }
        } else {
            layer.style.display = 'none';
            layer.style.backgroundImage = '';
            this.params.dom.classList.remove('studio-background');
            this.params.dom.classList.remove('studio-custom-background');
            ['white', 'warm', 'gray', 'dark'].forEach(preset => {
                this.params.dom.classList.remove(`studio-preset-${preset}`);
            });
            if (this.objects.supportLine && !this.three.scene.background) {
                this.objects.supportLine.visible = true;
            }
        }
        this.syncViewControllerMode();
    }

    getContainerList () {
        if (Array.isArray(this.objects.containers) && this.objects.containers.length) {
            return this.objects.containers;
        }
        return this.objects.container ? [this.objects.container] : [];
    }

    getContainerById (id) {
        if (!id) {
            return null;
        }
        return this.getContainerList().find(item => item.id === id) || null;
    }

    getActiveContainer () {
        return this.getContainerById(this.selectedContainerId)
            || this.objects.container
            || this.getContainerList()[0]
            || null;
    }

    getContainerGroupId (containerObject) {
        if (!containerObject) {
            return '';
        }
        return (containerObject.params && containerObject.params.groupId) || containerObject.id;
    }

    normalizeGroupRotation (rotationY) {
        let nextRotation = parseFloat(rotationY);
        if (Number.isNaN(nextRotation)) {
            nextRotation = 0;
        }
        nextRotation = nextRotation % 360;
        return nextRotation < 0 ? nextRotation + 360 : nextRotation;
    }

    normalizeContainerPosition (position = {}, fallback = {}) {
        const pickNumber = (value, defaultValue) => {
            const numberValue = parseFloat(value);
            return Number.isNaN(numberValue) ? (parseFloat(defaultValue) || 0) : numberValue;
        };
        return {
            x: pickNumber(position.x, fallback.x),
            y: pickNumber(position.y, fallback.y),
            z: pickNumber(position.z, fallback.z)
        };
    }

    getFlowersByContainerId (containerId) {
        const containerObject = this.getContainerById(containerId);
        const groupId = containerObject ? this.getContainerGroupId(containerObject) : containerId;
        return this.objects.flowers.filter(flowerObject => {
            const params = flowerObject.params || {};
            return params.containerId === containerId || (groupId && params.groupId === groupId);
        });
    }

    updateFlowersForContainer (containerId, sourceStartPosition) {
        for (let flowerObject of this.getFlowersByContainerId(containerId)) {
            flowerObject.clearReal(false);
            flowerObject.updateModel(sourceStartPosition);
        }
    }

    emitContainerSelection (containerObject) {
        const payload = this.getContainerEventPayload(containerObject);
        if (this.listener['container-add']) {
            this.listener['container-add'](payload);
        }
        if (this.listener['container-select']) {
            this.listener['container-select'](payload);
        }
        if (this.listener['studio-containers-change']) {
            this.listener['studio-containers-change'](this.getContainerLayers());
        }
    }

    refreshActiveContainer () {
        const containers = this.getContainerList();
        const nextContainer = this.getContainerById(this.selectedContainerId) || containers[containers.length - 1] || null;
        this.objects.container = nextContainer;
        this.selectedContainerId = nextContainer ? nextContainer.id : '';
    }

    selectContainerById (id) {
        const container = this.getContainerById(id);
        if (!container) {
            return null;
        }
        this.objects.container = container;
        this.selectedContainerId = container.id;
        this.selectedFlowerId = '';
        this.three.objController.detach();
        this.updateSelectionHelper();
        this.emitFlowerChoose(null);
        this.emitContainerSelection(container);
        this.syncLayerState();
        return container;
    }

    getContainerEventPayload (containerObject) {
        if (!containerObject) {
            return null;
        }
        return {
            id: containerObject.id,
            radius: containerObject.radius,
            height: containerObject.height,
            containerLight: !!containerObject.lightEnhanced,
            visible: containerObject.visible !== false,
            groupId: this.getContainerGroupId(containerObject),
            groupRotationY: this.normalizeGroupRotation(containerObject.params.groupRotationY),
            shadow: containerObject.params.shadow,
            params: containerObject.params,
            position: containerObject.position,
            rotation: containerObject.rotation,
            resourceId: containerObject.resource.id,
            resourceName: containerObject.resource.name,
            selected: containerObject.id === this.selectedContainerId
        };
    }

    getContainerLayers () {
        return this.getContainerList().map((containerObject, index) => ({
            ...this.getContainerEventPayload(containerObject),
            type: 'container',
            order: `器${index + 1}`,
            name: containerObject.resource.name || '容器',
            visible: containerObject.visible !== false,
            selected: !this.selectedFlowerId && containerObject.id === this.selectedContainerId,
            index: index + 1
        }));
    }

    getNextContainerPosition () {
        if (!this.isStudioMode()) {
            return { x: 0, y: 0, z: 0 };
        }
        const index = this.getContainerList().length;
        const offset = 48;
        return {
            x: (index % 3 - 1) * offset,
            y: Math.floor(index / 3) * offset,
            z: 0
        };
    }

    getBindingStartPosition (flowerObject) {
        if (!this.isStudioMode()) {
            return this.objects.jsContainer.position;
        }
        const containerId = flowerObject && flowerObject.params ? flowerObject.params.containerId : '';
        const containerObject = this.getContainerById(containerId) || this.getActiveContainer();
        if (containerObject && containerObject.getBindingPosition) {
            return containerObject.getBindingPosition();
        }
        return this.objects.jsContainer.position;
    }

    emitFlowerChoose (flowerObject = null) {
        if (!this.listener['flower-choose']) {
            return;
        }
        if (!flowerObject) {
            this.listener['flower-choose'](null);
            return;
        }
        this.listener['flower-choose']({
            id: flowerObject.id,
            height: flowerObject.modelMesh.flowerMesh.position.y,
            name: flowerObject.resource.name,
            coverImage: flowerObject.resource.coverImage,
            helperRect: this.getSelectionHelperScreenRect(flowerObject),
            ...flowerObject.params,
            resource: flowerObject.resource
        });
    }

    getFlowerLayers () {
        return this.objects.flowers.map((flowerObject, index) => ({
            ...flowerObject.getLayerData(),
            type: 'flower',
            order: index + 1,
            index: index + 1,
            containerId: flowerObject.params.containerId || '',
            selected: flowerObject.id === this.selectedFlowerId
        }));
    }

    getLayerItems () {
        return this.isStudioMode()
            ? [...this.getContainerLayers(), ...this.getFlowerLayers()]
            : this.getFlowerLayers();
    }

    syncLayerState () {
        if (this.listener['layer-change']) {
            this.listener['layer-change'](this.getLayerItems());
        }
    }

    getFlowerById (id) {
        return this.objects.flowers.find(item => item.id === id);
    }

    selectFlowerById (id, event) {
        const flowerObject = this.getFlowerById(id);
        if (!flowerObject || !flowerObject.params.visible) {
            this.clearFlowerSelection();
            return;
        }
        this.selectedFlowerId = flowerObject.id;
        if (this.isStudioMode() && flowerObject.params.containerId) {
            const bindContainer = this.getContainerById(flowerObject.params.containerId);
            if (bindContainer) {
                this.objects.container = bindContainer;
                this.selectedContainerId = bindContainer.id;
                this.emitContainerSelection(bindContainer);
            }
        }
        const controlObject = flowerObject.getControlObject ? flowerObject.getControlObject() : flowerObject.modelMesh.flowerMesh;
        if (flowerObject.params.locked) {
            this.three.objController.detach();
        } else if (event && controlObject !== this.three.objController.object) {
            this.three.objController.attach(controlObject, event);
        } else if (!event) {
            this.three.objController.detach();
        }
        this.updateSelectionHelper();
        this.emitFlowerChoose(flowerObject);
        this.syncLayerState();
    }

    clearFlowerSelection () {
        this.selectedFlowerId = '';
        this.three.objController.detach();
        this.updateSelectionHelper();
        this.emitFlowerChoose(null);
        this.syncLayerState();
    }

    clearLayerSelection () {
        this.selectedFlowerId = '';
        this.selectedContainerId = '';
        this.three.objController.detach();
        this.updateSelectionHelper();
        this.emitFlowerChoose(null);
        this.emitContainerSelection(this.getActiveContainer());
        this.syncLayerState();
    }

    selectLayerById (id) {
        if (this.getContainerById(id)) {
            this.selectContainerById(id);
            return;
        }
        this.selectFlowerById(id);
    }

    toggleFlowerVisible (id) {
        const flowerObject = this.getFlowerById(id);
        if (!flowerObject) {
            return;
        }
        flowerObject.setVisible(!flowerObject.params.visible);
        if (!flowerObject.params.visible && this.selectedFlowerId === id) {
            this.clearFlowerSelection();
            return;
        }
        if (this.three.status === 'real') {
            renderReal(this);
        } else {
            renderModel(this);
        }
        this.syncLayerState();
    }

    toggleLayerVisible (id) {
        const containerObject = this.getContainerById(id);
        if (containerObject) {
            containerObject.setVisible(containerObject.visible === false);
            if (containerObject.visible === false && this.selectedContainerId === id) {
                this.selectedContainerId = '';
            }
            renderModel(this);
            renderReal(this);
            this.emitContainerSelection(this.getActiveContainer());
            this.syncLayerState();
            return;
        }
        this.toggleFlowerVisible(id);
    }

    setFlowerStemVisible ({ id, value }) {
        const flowerObject = this.getFlowerById(id);
        if (!flowerObject) {
            return;
        }
        flowerObject.setStemVisible(value);
        if (this.three.status === 'real') {
            flowerObject.clearReal(false);
            renderReal(this);
        } else {
            renderModel(this);
        }
        if (this.selectedFlowerId === id) {
            this.emitFlowerChoose(flowerObject);
        }
        this.syncLayerState();
    }

    setFlowerStemRadius ({ id, value }) {
        const flowerObject = this.getFlowerById(id);
        if (!flowerObject) {
            return;
        }
        flowerObject.setStemRadius(value);
        if (this.three.status === 'real') {
            flowerObject.clearReal(false);
            renderReal(this);
        } else {
            this.render();
        }
        if (this.selectedFlowerId === id) {
            this.emitFlowerChoose(flowerObject);
        }
        this.syncLayerState();
    }

    setAllFlowerVisible (visible) {
        const containers = this.isStudioMode() ? this.getContainerList() : [];
        const hasVisibilityChange = this.objects.flowers.some((flowerObject) => {
            return flowerObject.params.visible !== visible;
        }) || containers.some(containerObject => containerObject.visible !== visible);
        if (!hasVisibilityChange) {
            return;
        }
        for (let flowerObject of this.objects.flowers) {
            flowerObject.setVisible(visible);
        }
        containers.forEach(containerObject => containerObject.setVisible(visible));
        if (!visible) {
            this.selectedContainerId = '';
            this.clearFlowerSelection();
            return;
        }
        if (this.three.status === 'real') {
            renderReal(this);
        } else {
            renderModel(this);
        }
        this.emitContainerSelection(this.getActiveContainer());
        this.syncLayerState();
    }

    toggleFlowerLock (id) {
        const flowerObject = this.getFlowerById(id);
        if (!flowerObject) {
            return;
        }
        flowerObject.setLocked(!flowerObject.params.locked);
        if (flowerObject.params.locked && this.selectedFlowerId === id) {
            this.three.objController.detach();
            this.emitFlowerChoose(flowerObject);
        }
        this.syncLayerState();
    }

    setAllFlowerLock (locked) {
        for (let flowerObject of this.objects.flowers) {
            flowerObject.setLocked(locked);
        }
        if (locked && this.selectedFlowerId) {
            this.three.objController.detach();
        }
        this.syncLayerState();
    }

    //控制剑山
    showJsContainer (show = false) {
        this.objects.jsContainer.visible = !!show;
        if (this.listener['jianshan-visible-change']) {
            this.listener['jianshan-visible-change'](this.objects.jsContainer.visible);
        }
    }

    //设置捆扎方式
    setBindingType (type) {
        new SetBindingTypeCommand(type || 'chineseStyle', this).do();
    }

    //设置触点
    setTouchHelper (show) {
        const visible = !!show;
        new SetTouchHelperCommand(visible, this).do();
        this.updateSelectionHelper();
        if (this.listener['touch-helper-change']) {
            this.listener['touch-helper-change'](visible);
        }
    }

    //设置捆扎高度
    setBindingY (params) {
        new SetBindingYCommand(params, this).do();
    }

    //设置整体捆扎高度偏移
    setAllBindingY (params) {
        new setAllBindingYCommand(params, this).do();
    }

    //设置花头大小
    setSize (params) {
        new SetSizeCommand(params, this).do();
    }

    ensureContainerLightGroup () {
        if (this.objects.containerLightGroup) {
            return this.objects.containerLightGroup;
        }
        const group = new THREE.Group();
        group.name = 'container-light-group';
        group.visible = false;

        const keyLight = new THREE.DirectionalLight(0xffffff, 0.42);
        keyLight.position.set(-0.65, 1.15, -0.75);
        keyLight.layers.set(CONTAINER_LIGHT_LAYER);
        group.add(keyLight);

        const fillLight = new THREE.HemisphereLight(0xffffff, 0xf0e4d8, 0.18);
        fillLight.layers.set(CONTAINER_LIGHT_LAYER);
        group.add(fillLight);

        this.three.scene.add(group);
        this.objects.containerLightGroup = group;
        return group;
    }

    withContainerLightOverlayMaterials (callback) {
        const lightContainers = this.getContainerList().filter(container => container.lightEnhanced && container.forEachMaterial);
        if (!lightContainers.length) {
            callback();
            return;
        }
        const snapshots = [];
        lightContainers.forEach(container => {
            container.forEachMaterial(material => {
                if (!material) {
                    return;
                }
                snapshots.push({
                    material,
                    transparent: material.transparent,
                    blending: material.blending,
                    depthWrite: material.depthWrite,
                    opacity: material.opacity,
                });
                material.transparent = true;
                material.blending = THREE.AdditiveBlending;
                material.depthWrite = false;
                if (typeof material.opacity === 'number') {
                    material.opacity = Math.min(material.opacity, CONTAINER_LIGHT_OVERLAY_PRESET.opacity);
                }
            });
        });
        try {
            callback();
        } finally {
            snapshots.forEach(snapshot => {
                snapshot.material.transparent = snapshot.transparent;
                snapshot.material.blending = snapshot.blending;
                snapshot.material.depthWrite = snapshot.depthWrite;
                snapshot.material.opacity = snapshot.opacity;
            });
        }
    }

    renderContainerLightPass () {
        const hasLightContainer = this.getContainerList().some(container => container.lightEnhanced);
        if (!hasLightContainer || !this.objects.containerLightGroup) {
            return;
        }
        const renderer = this.three.renderer;
        const camera = this.three.camera;
        const scene = this.three.scene;
        const cameraLayerMask = camera.layers.mask;
        const autoClear = renderer.autoClear;
        const sceneBackground = scene.background;

        try {
            renderer.autoClear = false;
            scene.background = null;
            camera.layers.set(CONTAINER_LIGHT_LAYER);
            this.withContainerLightOverlayMaterials(() => {
                renderer.render(scene, camera);
            });
        } finally {
            camera.layers.mask = cameraLayerMask;
            renderer.autoClear = autoClear;
            scene.background = sceneBackground;
        }
    }

    //设置器皿光感
    setContainerLight (enabled = false) {
        const targetContainer = this.getActiveContainer();
        if (targetContainer) {
            targetContainer.setLightEnhance(!!enabled);
        }
        const hasLightContainer = this.getContainerList().some(container => container.lightEnhanced);
        this.params.render.containerLight = !this.isStudioMode() && !!enabled;
        const lightGroup = hasLightContainer || this.params.render.containerLight
            ? this.ensureContainerLightGroup()
            : this.objects.containerLightGroup;
        if (lightGroup) {
            lightGroup.visible = hasLightContainer || this.params.render.containerLight;
        }
        this.render();
        if (targetContainer) {
            this.emitContainerSelection(targetContainer);
        }
    }

    //设置背景图片
    setBackground (url) {
        this.params.background = url || '';
        if (this.isStudioMode()) {
            this.three.scene.background = null;
            if (this.objects.supportLine) {
                this.objects.supportLine.visible = false;
            }
            return;
        }
        if (url) {
            var texture = new THREE.TextureLoader().load(url);
            texture.mapping = THREE.EquirectangularReflectionMapping;
            texture.colorSpace = THREE.SRGBColorSpace;
            this.three.scene.background = texture;
            this.objects.supportLine.visible = false;
        }
        else {
            this.three.scene.background = null;
            this.objects.supportLine.visible = true;
        }
    }

    //旋转
    rotate (params) {
        return new RotateCommand(params, this).do();
    }

    //设置放大倍数
    setZoom (val) {
        new SetZoomCommand(val, this).do();
    }

    //设置角度
    setAngle (params) {
        new SetAngleCommand(params, this).do();
    }

    //图层展示
    layerShow (id) {
        this.params.showId = id;
        renderModel(this);
        renderReal(this);
        this.syncLayerState();
    }

    //清除
    clear (type) {
        new ClearCommand(type, this).do();
        this.syncLayerState();
    }

    //撤销
    revoke () {
        this.commandManager.revoke()
    }

    //重做
    redo () {
        this.commandManager.redo();
    }

    //添加物体
    async addItem (params) {
        console.log('addItem')
        const addedId = await this.commandManager.execute(new AddItemCommand(params instanceof Object ? params : { resourceId: params }, this));
        if (this.getFlowerById(addedId)) {
            this.selectFlowerById(addedId);
        }
        return addedId;
    }

    //删除物体
    remove (id) {
        return this.commandManager.execute(new ReomveCommand(id, this));
    }

    //拷贝物体
    async copy (id) {
        const copiedId = await this.commandManager.execute(new CopyCommand(id, this));
        if (this.getFlowerById(copiedId)) {
            this.selectFlowerById(copiedId);
        }
        return copiedId;
    }

    //替换
    replace (params) {
        return this.commandManager.execute(new ReplaceCommand(params, this));
    }

    //设置初始/剑山位置
    setStartPosition (params) {
        let sourceStartPosition = this.objects.jsContainer.position.clone();
        let vec;
        if (params.type === 'relative') {
            vec = new THREE.Vector3(params.position.x, params.position.y, params.position.z);
            vec.applyEuler(this.three.viewController.object.rotation).projectOnPlane(new THREE.Vector3(0, 1, 0));
        }
        this.objects.jsContainer.position.x += (vec.x || 0);
        this.objects.jsContainer.position.y += (vec.y || 0);
        this.objects.jsContainer.position.z += (vec.z || 0);

        for (let flower of this.objects.flowers) {
            flower.clearReal(false);
            flower.updateModel(sourceStartPosition);
        }
        renderReal(this);
    }

    setContainerPosition (params = {}) {
        const containerObject = this.getContainerById(params.id) || this.getActiveContainer();
        if (!containerObject) {
            return;
        }
        const sourcePosition = containerObject.getBindingPosition();
        const nextPosition = params.type === 'relative'
            ? {
                x: containerObject.position.x + (parseFloat(params.position && params.position.x) || 0),
                y: containerObject.position.y + (parseFloat(params.position && params.position.y) || 0),
                z: containerObject.position.z + (parseFloat(params.position && params.position.z) || 0)
            }
            : this.normalizeContainerPosition(params.position || params, containerObject.position);
        containerObject.setPosition(nextPosition);
        this.updateFlowersForContainer(containerObject.id, sourcePosition);
        this.objects.container = containerObject;
        this.selectedContainerId = containerObject.id;
        renderReal(this);
        this.emitContainerSelection(containerObject);
    }

    setContainerRotation (params = {}) {
        const containerObject = this.getContainerById(params.id) || this.getActiveContainer();
        if (!containerObject) {
            return;
        }
        containerObject.setRotation(params.rotation || params);
        this.objects.container = containerObject;
        this.selectedContainerId = containerObject.id;
        this.render();
        this.emitContainerSelection(containerObject);
    }

    setContainerShadow (params = {}) {
        const containerObject = this.getContainerById(params.id) || this.getActiveContainer();
        if (!containerObject || !containerObject.setShadow) {
            return;
        }
        containerObject.setShadow(params.shadow || params);
        this.objects.container = containerObject;
        this.selectedContainerId = containerObject.id;
        this.render();
        this.emitContainerSelection(containerObject);
    }

    rotatePositionAroundCenter (position, center, deltaRad) {
        const offsetX = position.x - center.x;
        const offsetZ = position.z - center.z;
        const cos = Math.cos(deltaRad);
        const sin = Math.sin(deltaRad);
        position.x = center.x + offsetX * cos - offsetZ * sin;
        position.z = center.z + offsetX * sin + offsetZ * cos;
    }

    setStudioGroupRotation (params = {}) {
        if (!this.isStudioMode()) {
            return;
        }
        const containerObject = this.getContainerById(params.id) || this.getActiveContainer();
        if (!containerObject || !containerObject.setGroupRotationY) {
            return;
        }
        const nextRotation = this.normalizeGroupRotation(params.rotationY);
        const currentRotation = this.normalizeGroupRotation(containerObject.params.groupRotationY);
        let delta = nextRotation - currentRotation;
        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }
        if (Math.abs(delta) < 0.0001) {
            return;
        }
        const center = containerObject.getBindingPosition();
        const groupId = this.getContainerGroupId(containerObject);
        for (let flowerObject of this.getFlowersByContainerId(containerObject.id)) {
            const params = flowerObject.params || {};
            params.containerId = containerObject.id;
            params.groupId = groupId;
            flowerObject.clearReal(false);
            this.rotatePositionAroundCenter(flowerObject.modelMesh.flowerMesh.position, center, THREE.MathUtils.degToRad(delta));
            flowerObject.updateModel();
        }
        containerObject.setGroupRotationY(nextRotation);
        this.objects.container = containerObject;
        this.selectedContainerId = containerObject.id;
        if (params.deferReal) {
            renderModel(this);
        } else {
            renderReal(this);
        }
        this.emitContainerSelection(containerObject);
        this.syncLayerState();
    }

    startStudioGroupDrag (event) {
        if (
            !this.isStudioMode()
            || this.params.readOnly
            || this.params.canvasLock
            || this.params.moving
            || (event.button !== undefined && event.button !== 0)
        ) {
            return;
        }
        if (pickFlowerObject.call(this, event)) {
            return;
        }
        const containerObject = pickContainerObject.call(this, event);
        if (!containerObject) {
            return;
        }
        this.selectContainerById(containerObject.id);
        this.studioGroupDrag = {
            active: true,
            pointerId: event.pointerId,
            containerId: containerObject.id,
            startX: event.clientX,
            startRotationY: this.normalizeGroupRotation(containerObject.params.groupRotationY)
        };
        if (event.currentTarget && event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        event.preventDefault();
    }

    updateStudioGroupDrag (event) {
        if (!this.studioGroupDrag.active || this.studioGroupDrag.pointerId !== event.pointerId) {
            return;
        }
        const deltaX = event.clientX - this.studioGroupDrag.startX;
        this.setStudioGroupRotation({
            id: this.studioGroupDrag.containerId,
            rotationY: this.studioGroupDrag.startRotationY + deltaX * 0.45,
            deferReal: true
        });
        event.preventDefault();
        event.stopPropagation();
    }

    endStudioGroupDrag (event) {
        if (!this.studioGroupDrag.active || this.studioGroupDrag.pointerId !== event.pointerId) {
            return;
        }
        this.studioGroupDrag = {
            active: false,
            pointerId: null,
            containerId: '',
            startX: 0,
            startRotationY: 0
        };
        if (event.currentTarget && event.currentTarget.releasePointerCapture && event.pointerId !== undefined) {
            try {
                event.currentTarget.releasePointerCapture(event.pointerId);
            } catch (error) {
                // 指针已释放时浏览器会抛错，忽略即可。
            }
        }
        renderReal(this);
    }

    getArtworkData () {
        let y = THREE.MathUtils.radToDeg(this.three.viewController.getAzimuthalAngle()),
            x = THREE.MathUtils.radToDeg(this.three.viewController.getPolarAngle());
        if (y < 0) {
            y = 180 + (180 + y);
        }

        let flowers = [];
        for (let flowerObject of this.objects.flowers) {
            const fPosition = flowerObject.modelMesh.flowerMesh.position;
            flowers.push({
                id: flowerObject.id,
                resourceId: flowerObject.resource.id,
                resourceName: flowerObject.resource.name,
                position: { x: fPosition.x, y: fPosition.y, z: fPosition.z },
                containerId: flowerObject.params.containerId || '',
                params: flowerObject.params
            })
        }

        const containers = this.getContainerList()
            .map(containerObject => getContainerSaveData(containerObject))
            .filter(Boolean);
        const container = this.isStudioMode()
            ? (containers.find(item => item.id === this.selectedContainerId) || containers[0] || null)
            : getContainerSaveData(this.objects.container);

        const jsContainer = {
            visible: this.objects.jsContainer.visible,
            size: this.objects.jsContainer.scale.x,
            position: this.objects.jsContainer.position
        };

        let jsonData = {
            objects: {
                flowers,
                container,
                containers: this.isStudioMode() ? containers : undefined,
                selectedContainerId: this.isStudioMode() ? this.selectedContainerId : undefined,
                jsContainer
            },
            angle: {
                x,
                y
            },
            params: {
                editorType: this.params.editorType,
                render: {
                    bindingType: this.params.render.bindingType,
                    touchHelper: this.params.render.touchHelper,
                    containerLight: this.params.render.containerLight
                },
                background: this.params.background,
                studio: this.params.studio
            }
        }
        return jsonData;
    }

    //保存
    async save (params) {
        let jsonData = this.getArtworkData();
        const supportLineVisible = this.objects.supportLine.visible;
        const selectionHelperSuspended = this.selectionHelperSuspended;

        this.selectionHelperSuspended = true;
        this.updateSelectionHelper();
        try {
            this.objects.supportLine.visible = false;
            this.render();
            let base64;
            if (!params) {
                base64 = await new CreateImageCommand({
                    width: 500,
                    height: 500
                }, this).do();
            } else {
                base64 = [];
                for (let angle of params.angles) {
                    await new SetAngleCommand(angle, this).do();
                    this.render();
                    base64.push(await new CreateImageCommand({
                        width: 500,
                        height: 500
                    }, this).do())
                }
            }
            return {
                jsonData,
                base64
            }
        } finally {
            this.objects.supportLine.visible = supportLineVisible;
            this.selectionHelperSuspended = selectionHelperSuspended;
            this.updateSelectionHelper();
        }
    }

    async load (jsonData) {

        const params = jsonData.params || {};
        this.setBackground(params.background || "");
        if (params.studio) {
            this.params.studio = {
                ...this.params.studio,
                ...params.studio,
                backgroundAdjust: createStudioBackgroundAdjust(params.studio.backgroundAdjust)
            };
        }
        const renderParams = (params && params.render) || {};
        this.setEditorType(params.editorType || ((jsonData.objects && jsonData.objects.containers) ? 'studio' : 'free3d'));
        this.params.render.bindingType = renderParams.bindingType || 'chineseStyle';
        this.setTouchHelper(renderParams.touchHelper !== false);
        if (!this.isStudioMode()) {
            this.setContainerLight(renderParams.containerLight);
        }
        const jsContainerData = (jsonData.objects && jsonData.objects.jsContainer) || {};
        // 进入编辑器时默认隐藏剑山，避免旧草稿里的显示状态覆盖新的默认体验。
        this.showJsContainer(false);
        if (jsContainerData.position) {
            this.objects.jsContainer.position.x = jsContainerData.position.x;
            this.objects.jsContainer.position.y = jsContainerData.position.y;
            this.objects.jsContainer.position.z = jsContainerData.position.z;
        }
        new SetSizeCommand({ id: 'jianshan', value: jsContainerData.size || 1 }, this).do();

        const objectData = jsonData.objects || {};
        const containerList = this.isStudioMode()
            ? (objectData.containers || (objectData.container ? [objectData.container] : []))
            : (objectData.container ? [objectData.container] : []);

        for (let containerData of containerList) {
            await new AddItemCommand(containerData, this).do();
        }

        if (this.isStudioMode() && objectData.selectedContainerId) {
            this.selectContainerById(objectData.selectedContainerId);
        }

        for (let flower of (objectData.flowers || [])) {
            const flowerParams = {
                ...(flower.params || {})
            };
            if (flower.containerId && !flowerParams.containerId) {
                flowerParams.containerId = flower.containerId;
            }
            await new AddItemCommand({
                ...flower,
                params: flowerParams
            }, this).do();
        }

        if (this.isStudioMode() && objectData.selectedContainerId) {
            this.selectContainerById(objectData.selectedContainerId);
        }
        this.syncLayerState();

    }

    //画布锁定
    canvasLock (val) {
        this.params.canvasLock = val;
        this.syncViewControllerMode();
    }

    //渲染真实场景
    renderRealScene () {
        renderReal(this);
    }
}

export default Editor;
